import { capitalizeFirst } from './capitalize';

// Display labels for the classification's closed vocabularies (apps/api/shared/utils/
// classification.py) that the event page and cards show (#684). Each map holds one literal
// key per value, so `pnpm i18n:check` sees every one; `t` is the caller's translator (the
// keys carry their `labels:` namespace). A value a map does not hold falls through to
// `formatVocabularyValue`. Labels are sentence case in every language ("Live music"),
// acronyms kept ("DJ", "XL") — the catalog carries the casing, never CSS or a title-caser.

// The one fallback for a raw value the catalog does not hold — vocabulary ids, language
// names, event types, chips: sentence case like every catalog label, "young_adults" →
// "Young adults", with "xl" kept an acronym.
export const formatVocabularyValue = (value) => {
  if (!value) return '';
  const words = value.toString().replace(/[_-]/g, ' ').split(' ').filter(Boolean)
    .map((part) => part.toLowerCase() === 'xl' ? 'XL' : part.toLowerCase());
  return capitalizeFirst(words.join(' '), 'en');
};

// A label getter over a map of literal-key thunks: the catalog's label for a value the map
// holds (own keys only), `fallback(value)` for anything else.
export const labelFrom = (labels, fallback = formatVocabularyValue) => (t, value) =>
  (Object.hasOwn(labels, value) ? labels[value](t) : fallback(value));

// `experience_modalities`, the format badge on cards and the event hero.
const FORMAT_LABELS = {
  live_music: (t) => t('labels:formats.live_music'),
  dj_set: (t) => t('labels:formats.dj_set'),
  film_screening: (t) => t('labels:formats.film_screening'),
  theater_performance: (t) => t('labels:formats.theater_performance'),
  dance_performance: (t) => t('labels:formats.dance_performance'),
  comedy_performance: (t) => t('labels:formats.comedy_performance'),
  circus_performance: (t) => t('labels:formats.circus_performance'),
  magic_performance: (t) => t('labels:formats.magic_performance'),
  classical_music: (t) => t('labels:formats.classical_music'),
  exhibition_viewing: (t) => t('labels:formats.exhibition_viewing'),
  talk_panel: (t) => t('labels:formats.talk_panel'),
  workshop_hands_on: (t) => t('labels:formats.workshop_hands_on'),
  networking: (t) => t('labels:formats.networking'),
  sports_competition: (t) => t('labels:formats.sports_competition'),
  market_fair: (t) => t('labels:formats.market_fair'),
  food_drink: (t) => t('labels:formats.food_drink'),
  community_meetup: (t) => t('labels:formats.community_meetup'),
  guided_tour: (t) => t('labels:formats.guided_tour'),
  ceremony: (t) => t('labels:formats.ceremony'),
  digital_interactive: (t) => t('labels:formats.digital_interactive'),
};

// The catalog's label for a known format, or null — the badge words anything else itself.
export const getFormatLabel = labelFrom(FORMAT_LABELS, () => null);

// `target_audience`, the About section's audience line.
export const getTargetAudienceLabel = labelFrom({
  children: (t) => t('labels:audiences.children'),
  teens: (t) => t('labels:audiences.teens'),
  young_adults: (t) => t('labels:audiences.young_adults'),
  adults: (t) => t('labels:audiences.adults'),
  families: (t) => t('labels:audiences.families'),
  general_public: (t) => t('labels:audiences.general_public'),
  professionals: (t) => t('labels:audiences.professionals'),
  students: (t) => t('labels:audiences.students'),
  tourists: (t) => t('labels:audiences.tourists'),
  locals: (t) => t('labels:audiences.locals'),
});

// The venue's `capacity_bucket`, inside `event:venueCapacity`.
export const getCapacityLabel = labelFrom({
  small: (t) => t('labels:capacity.small'),
  medium: (t) => t('labels:capacity.medium'),
  large: (t) => t('labels:capacity.large'),
  xl: (t) => t('labels:capacity.xl'),
  massive: (t) => t('labels:capacity.massive'),
});

// The API's `capacity_range_bounds` (#768), inside `event:venueCapacity`: a first bucket
// (min 1) reads "Under {max}", the open top one "{min}+". No threshold is known here.
// Every number is grouped, a product decision: CLDR es printed "5000 – 20.000" in one range.
// One formatter per language, like `formatList`.
const capacityFormats = new Map();
export const getCapacityRangeLabel = (t, bounds, locale) => {
  if (!Number.isFinite(bounds?.min)) return null;
  if (!capacityFormats.has(locale)) {
    capacityFormats.set(locale, new Intl.NumberFormat(locale, { useGrouping: 'always' }));
  }
  const { format } = capacityFormats.get(locale);
  if (bounds.max == null) return t('labels:capacityRange.plus', { min: format(bounds.min) });
  if (bounds.min <= 1) return t('labels:capacityRange.under', { max: format(bounds.max) });
  return t('labels:capacityRange.between', { min: format(bounds.min), max: format(bounds.max) });
};

// The venue's `disposition`.
export const getDispositionLabel = labelFrom({
  seated: (t) => t('labels:disposition.seated'),
  standing: (t) => t('labels:disposition.standing'),
});

